// Polling hook for a BookStructure.
//
// Lifecycle:
//   - On mount (or id change) fetches the structure.
//   - While `proposing === true`, polls every 3s until either:
//       - proposing flips to false (success or error → stop polling), OR
//       - the component unmounts / id changes (cleanup).
//   - Exposes refetch() so callers can force-refresh after a write
//     (e.g. after PATCH or approve).
//
// Designed for the BookStructureCard on the SourceDocument detail page
// AND the SourceDocumentMapPage tree editor — both need the same
// "render whatever we have, keep polling while proposing" semantics.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { BookStructure } from '@gc-coach/shared';
import { getBookStructure } from '../api/endpoints.js';

const POLL_INTERVAL_MS = 3_000;

export interface UseBookStructureResult {
  structure: BookStructure | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useBookStructure(id: string | null | undefined): UseBookStructureResult {
  const [structure, setStructure] = useState<BookStructure | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  const fetchOnce = useCallback(async (): Promise<BookStructure | null> => {
    if (!id) return null;
    const result = await getBookStructure(id);
    if (result.ok) {
      if (!cancelledRef.current) {
        setStructure(result.data.bookStructure);
        setError(null);
      }
      return result.data.bookStructure;
    }
    if (!cancelledRef.current) {
      setError(result.message);
    }
    return null;
  }, [id]);

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async () => {
    const fresh = await fetchOnce();
    if (cancelledRef.current) return;
    if (fresh && fresh.proposing) {
      timerRef.current = window.setTimeout(pollOnce, POLL_INTERVAL_MS);
    }
  }, [fetchOnce]);

  // Mount / id-change effect: initial fetch + maybe start polling.
  useEffect(() => {
    cancelledRef.current = false;
    stopPolling();

    if (!id) {
      setStructure(null);
      setError(null);
      return;
    }
    setIsLoading(true);
    void (async () => {
      const fresh = await fetchOnce();
      if (cancelledRef.current) return;
      setIsLoading(false);
      if (fresh && fresh.proposing) {
        timerRef.current = window.setTimeout(pollOnce, POLL_INTERVAL_MS);
      }
    })();

    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, [id, fetchOnce, pollOnce, stopPolling]);

  const refetch = useCallback(async () => {
    // Allow caller-triggered refresh; if the result is proposing,
    // resume polling.
    cancelledRef.current = false;
    stopPolling();
    const fresh = await fetchOnce();
    if (cancelledRef.current) return;
    if (fresh && fresh.proposing) {
      timerRef.current = window.setTimeout(pollOnce, POLL_INTERVAL_MS);
    }
  }, [fetchOnce, pollOnce, stopPolling]);

  return { structure, isLoading, error, refetch };
}
